import createDebug from "debug";
import { Deserializer } from "jsonapi-serializer";
import { NO_CONTENT } from "../constants/response-status-codes";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { Site } from "../models/Site";
import { SiteCode } from "../models/SiteCode";
import { SiteName } from "../models/SiteName";
import { SiteWrapper } from "../models/wrappers/SiteWrapper";
import { TableWrapper } from "../models/wrappers/TableWrapper";
import { validatePostAndPatchResponse } from "../utilities/requestResponseValidator";
import { APIConnector } from "./api/APIConnector";
import { createPagination } from "./utilities/paginationBuilder";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";

const debug = createDebug("site-service");
const apiConnector = new APIConnector();

const compileFilters = (siteId, listParameters) => {
    const siteFilter = {
        key: "site.id",
        label: "Site", // not displayed
        options: [{ label: siteId, value: siteId }],
        selected: [{ label: siteId, value: siteId }],
    };
    return getFilterClause(
        [...(listParameters?.filters ?? []), siteFilter],
        listParameters?.searchTerm
    );
};

export const saveSiteCode = async (siteId: string, siteCode: number, createNewSiteCode = true, id?): Promise<{ id: string; success: boolean }> => {
    try {
        const url = `siteCode${createNewSiteCode ? "" : `/${id}`}`;
        const newSiteCode = {
            data: {
                type: "siteCode",
                id: id ? id : 0,
                attributes: {
                    siteCode: siteCode,
                },
                relationships: {
                    site: {
                        data: {
                            type: "site",
                            id: siteId,
                        },
                    },
                },
            },
        };
        let response;

        await apiConnector
            .sendData(url, createNewSiteCode, newSiteCode)
            .then((data) => {
                response = data;
            })
            .catch((error) => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                })
            });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {


                if (createNewSiteCode) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({status: response.status, message: content.errors.map(error => getConstraintViolation(error.detail)) })
                    }

                    new Deserializer().deserialize(content, (error, newFloor) => {
                        if (error) {
                            return reject({status: response.status, id: id, success: false });
                        }
                        return resolve({ id: newFloor.id, success: true });
                    })

                    return;
                }
                if (response.status === NO_CONTENT) {
                    return resolve({ id: id, success: true })
                }

                const content = await response.json();
                return reject({status: response.status, id: id, success: false, message: getConstraintViolation(content.errors[0].detail) })
            }

            return reject({status: response.status, success: false, message: genericError });

        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getSiteCodesBySiteId = async (listParameters?: ListQuery, siteId?: string): Promise<TableWrapper> => {
    try {
        let filters: string = compileFilters(siteId, listParameters);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
        const siteCodeFields = 'fields[siteCode]=siteCode,site';
        const siteFields = 'fields[site]=siteName';
        const url = `siteCode?include=site${paginationString}&${siteCodeFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (err: any, apiSiteCodes: any[]) => {
                    if (err) {
                        return reject({status: response.status, message: genericError });
                    }

                    const siteCodes: SiteCode[] = apiSiteCodes.map(
                        (apiSiteCode) => {
                            return {
                                id: apiSiteCode["id"],
                                code: apiSiteCode["site-code"],
                                site: apiSiteCode["site"]["site-name"],
                                siteId: apiSiteCode["site"]["id"],
                            };
                        }
                    );

                    return resolve({ rows: siteCodes, pagination });
                }
            );
        });

    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const storeSite = async (site: Site, createNewSite = true) => {
    try {
        const newRecord = {
            data: {
                type: "site",
                id: site.id,
                attributes: {
                    // siteAlarmControllers: "0000000000000000000000000000000000000000000000000000000000000000",
                    siteAnytag: 1,
                    // siteArmingDelayTime: 30,
                    // siteArmingTimeout: 3,
                    siteCommon: 1,
                    siteCommsMode: 0,
                    siteDefault: site.default ?? 0,
                    // siteDisarmingTimeout: 3,
                    // siteDisarmRequestType: 0,
                    // siteEnforcerouting: 1,
                    // siteGroupword: 0,
                    siteHostip: site.hostIP,
                    // siteHostport: 0,
                    siteLocal: 0,
                    // siteMaskingCodeModified: 0,
                    siteName: site.name,
                    // sitePinLockoutTime: 40,
                    siteSeed: "0",
                    siteTagmask: site.tagmask,
                    siteTagmaskRemovezeros: site.tagmaskRemovezeros,
                    siteUdp: 1,
                    wanIpAddress: site.wanIP,
                    siteDescription: site.description,
                    siteOssid: site.ossId,
                    siteOssValidityDur: site.ossValidityDuration
                },
                relationships: {
                    timeZone: {
                        data: {
                            type: "timeZone",
                            id: site.timeZoneId,
                        },
                    },
                },
            },
        };

        const url = `site${createNewSite ? "" : `/${newRecord.data.id}`}`;
        let response;

        await apiConnector
            .sendData(url, createNewSite, newRecord)
            .then((data) => {
                response = data;
            })
            .catch((error) => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                })
            });

        return validatePostAndPatchResponse(response, newRecord.data.id);
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getSiteById = async (id: string): Promise<Site> => {
    try {
        const siteFields = 'fields[site]=siteName,siteHostip,wanIpAddress,siteTagmask,siteTagmaskRemovezeros,timeZone,siteDefault,isDefault,siteDescription,siteOssValidityDur,siteOssid';
        const timeZoneFields = 'fields[timeZone]=zone';
        const url = `site/${id}?include=timeZone&${siteFields}&${timeZoneFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(json, (err: any, apiSite: any) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const site: Site = buildSite(apiSite);

                return resolve(site);
            });

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getSiteNamesWithPagination = async (listQuery?: ListQuery, dropSystemSite = true): Promise<{ rows: Name[]; pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const filterOutSystemSite = dropSystemSite ? "filter=id!=1" : "";
        const updatedFilters = filters.length > 0 ? filters.replace("filter=", `${filterOutSystemSite};`) : `&${filterOutSystemSite}`;
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, dropSystemSite ? updatedFilters : filters);
        const siteFields = 'fields[site]=siteName,isDefault';
        let url = "site" + (paginationString ? `?${paginationString.substring(1)}&${siteFields}` : "");
        url = url.replace("site.siteName", "siteName");
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(
                json,
                (err: any, apiSites: any[]) => {
                    if (err) {
                        debug("getSites", err);
                        return reject({status: response.status, message: genericError });
                    }

                    const sites: Name[] = apiSites.map(
                        (site): SiteName => {
                            return {
                                value: site["id"],
                                label: site["site-name"],
                                isDefault: site['is-default']
                            };
                        }
                    );

                    return resolve({ rows: sites, pagination: newPagination });
                }
            );

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getSiteNames = async (dropSystemSite = true): Promise<SiteName[]> => {
    try {
        const filterOutSystemSite = dropSystemSite ? "?filter=id!=1" : "";
        const siteFields = "fields[site]=siteName,isDefault";
        const url = `site${filterOutSystemSite}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(
                json,
                (err: any, apiSites: any[]) => {
                    if (err) {
                        debug("getSites", err);
                        return reject({status: response.status, message: genericError });
                    }

                    const sites: SiteName[] = apiSites.map(
                        (site): SiteName => {
                            return {
                                value: site["id"],
                                label: site["site-name"],
                                isDefault: site['is-default']
                            };
                        }
                    );

                    return resolve(sites);
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getAllSiteNames = async (): Promise<SiteName[]> => {
    try {
        const url = `site?fields[site]=siteName`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(json, (err: any, apiSites: any[]) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const sites: SiteName[] = apiSites.map(
                    (site): SiteName => {
                        return {
                            value: site["id"],
                            label: site["site-name"],
                            isDefault: site['is-default']
                        };
                    }
                );

                return resolve(sites);
            }
            );

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getSites = async (listParameters?: ListQuery, dropSystemSite = true): Promise<SiteWrapper> => {
    try {
        const withFilters = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const filterOutSystemSite = dropSystemSite ? `${withFilters ? withFilters.replace("filter=", "filter=id!=1;") : "&filter=id!=1"}` : withFilters;
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filterOutSystemSite);
        const fieldsSites = "fields[site]=siteName,siteHostip,siteTagmask,isDefault,timeZone";
        const fieldTimeZone = "fields[timeZone]=zone";
        const url = `site?include=timeZone${partialURL}&${fieldsSites}&${fieldTimeZone}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const paginationModel = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(json, (err: any, apiSites: any[]) => {
                if (err) {
                    return reject({status: response.status, message: genericError });
                }

                const sites: Site[] = apiSites.map(buildSite);

                return resolve({
                    rows: sites,
                    pagination: paginationModel,
                });

            });

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const deleteSiteCode = async (id: string): Promise<{ id: string; success: boolean }> => {
    try {
        const url = `siteCode/${id}`;
        const siteCode = {
            data: {
                type: "siteCode",
                id: id,
            },
        };
        const response = await apiConnector.deleteData(url, siteCode);

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true });
            }

            const json = await response.json();
            reject({status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

const buildSite = (site): Site => {
    return {
        id: site["id"],
        name: site["site-name"],
        nameOriginal: site["site-name"],
        hostIP: site["site-hostip"],
        wanIP: site["wan-ip-address"] ? site["wan-ip-address"] : '',
        tagmask: site["site-tagmask"],
        tagmaskRemovezeros: site["site-tagmask-removezeros"],
        timeZone: site["time-zone"]["zone"],
        timeZoneId: site["time-zone"]["id"],
        default: site["site-default"],
        isDefault: site['is-default'],
        description: site['site-description'] ?? '',
        ossId: site['site-ossid'] ?? 0,
        ossValidityDuration: site['site-oss-validity-dur']
    };
};

export const deleteSite = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `site/${id}`;
        const holiday = {
            data: {
                type: "site",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, holiday)

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
               return resolve({ id: id, success: true })
            }
            const json = await response.json();
            return reject({status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
